import { useEffect } from "react"
import { X } from "lucide-react"
import type { CatalogItem } from "../types"
import { FEATURE_INFO, durationMonths, featureList, formatPrice, includesPremium, savingNet } from "../lib/format"
import { InfoHint, PremiumBadge, SaveBadge, Tick } from "./ui"

interface Props {
  item: CatalogItem | null
  catalog: CatalogItem[]
  onClose: () => void
  onAdd: (item: CatalogItem) => void
}

/**
 * Einstieg von der Produktwebsite (?produkt=<lookup_key>): zeigt das verlinkte
 * Produkt als Dialog ueber der Startseite, statt den Kunden suchen zu lassen.
 */
export function DeeplinkDialog({ item, catalog, onClose, onAdd }: Props) {
  useEffect(() => {
    if (!item) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [item, onClose])

  if (!item) return null

  const months = durationMonths(item.duration) ?? (item.kind === "bundle" ? 12 : null)
  const saving = savingNet(item, catalog)

  return (
    <div className="fixed inset-0 z-40 grid items-start justify-center overflow-y-auto p-5 pt-16 md:pt-40">
      <div className="fixed inset-0 bg-[rgba(60,58,64,0.6)]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={item.name}
        className="relative z-10 w-[520px] max-w-[calc(100vw-40px)] rounded-card border border-line bg-white p-5 shadow-panel md:p-6"
      >
        <button
          onClick={onClose}
          aria-label="Schließen"
          className="absolute right-5 top-5 rounded-full p-1 text-brand-muted hover:bg-surface md:right-6 md:top-6"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-wrap items-center gap-3 pr-8">
          <h3 className="w-full text-base font-medium md:w-auto md:text-lg">{item.name}</h3>
          {includesPremium(item) && <PremiumBadge />}
        </div>

        <p className="mt-4 text-sm text-brand-muted md:text-base">{item.description}</p>

        <div className="my-6 h-px bg-line" />

        <ul className="flex flex-col gap-1.5">
          {featureList(item).map((f) => (
            <li key={f} className="flex items-center gap-2 py-1 text-sm font-medium text-brand-card md:text-base">
              <Tick className="h-5 w-5" />
              <span>{f}</span>
              {FEATURE_INFO[f] && <InfoHint text={FEATURE_INFO[f]} />}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap items-center gap-2 md:flex-nowrap">
          <span className="w-full text-sm font-medium md:w-auto md:flex-1 md:whitespace-nowrap md:text-base">
            {months ? `Preis für ${months} Monate` : "Preis"}
          </span>
          {saving != null && <SaveBadge amount={formatPrice(saving, item.currency)} />}
          <span className="ml-auto whitespace-nowrap text-xl font-semibold text-brand-blue md:text-3xl">
            {formatPrice(item.amount_net, item.currency)}
          </span>
        </div>

        <button
          onClick={() => onAdd(item)}
          className="mt-6 w-full rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105"
        >
          In den Warenkorb
        </button>
      </div>
    </div>
  )
}
